import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { customerApi, CUSTOMER_TOKEN_KEY, formatStoreError } from "@/lib/customerApi";

const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null); // null = checking, false = guest, obj = signed-in
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    // Validate stored token shape before calling /me — prevents wasted requests
    // and Authorization headers built from injected garbage.
    let hasValidToken = false;
    if (typeof window !== "undefined") {
      try {
        const raw = localStorage.getItem(CUSTOMER_TOKEN_KEY);
        hasValidToken = !!raw && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(raw);
        if (raw && !hasValidToken) localStorage.removeItem(CUSTOMER_TOKEN_KEY);
      } catch { /* noop */ }
    }
    if (!hasValidToken) {
      setCustomer(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await customerApi.get("/me");
      setCustomer(data);
    } catch (err) {
      // Token expired or invalid — drop and continue as guest
      console.warn("Customer session check failed; clearing token", err?.message || err);
      try { localStorage.removeItem(CUSTOMER_TOKEN_KEY); } catch (storageErr) {
        console.warn("Failed to clear customer token", storageErr);
      }
      setCustomer(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  const signIn = async ({ email, password }) => {
    try {
      const { data } = await customerApi.post("/login", { email, password });
      if (data.access_token) {
        localStorage.setItem(CUSTOMER_TOKEN_KEY, data.access_token);
      }
      setCustomer(data.customer);
      return { ok: true, customer: data.customer };
    } catch (err) {
      return { ok: false, error: formatStoreError(err) };
    }
  };

  const signUp = async ({ email, password, name, phone, marketing_consent }) => {
    try {
      const { data } = await customerApi.post("/register", { email, password, name, phone, marketing_consent });
      if (data.access_token) {
        localStorage.setItem(CUSTOMER_TOKEN_KEY, data.access_token);
      }
      setCustomer(data.customer);
      return { ok: true, customer: data.customer };
    } catch (err) {
      return { ok: false, error: formatStoreError(err) };
    }
  };

  const signOut = async () => {
    try { await customerApi.post("/logout"); } catch (err) {
      // Logout is best-effort — local cleanup still proceeds.
      console.warn("Customer logout request failed", err?.message || err);
    }
    try { localStorage.removeItem(CUSTOMER_TOKEN_KEY); } catch (err) {
      console.warn("Failed to clear customer token", err);
    }
    setCustomer(false);
  };

  const updateProfile = async (patch) => {
    try {
      const { data } = await customerApi.put("/me", patch);
      setCustomer(data);
      return { ok: true, customer: data };
    } catch (err) {
      return { ok: false, error: formatStoreError(err) };
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      const { data } = await customerApi.post("/forgot-password", { email });
      return { ok: true, ...data };
    } catch (err) {
      return { ok: false, error: formatStoreError(err) };
    }
  };

  const resetPassword = async ({ token, new_password }) => {
    try {
      const { data } = await customerApi.post("/reset-password", { token, new_password });
      if (data.access_token) {
        localStorage.setItem(CUSTOMER_TOKEN_KEY, data.access_token);
      }
      if (data.customer) setCustomer(data.customer);
      return { ok: true, customer: data.customer };
    } catch (err) {
      return { ok: false, error: formatStoreError(err) };
    }
  };

  const changePassword = async ({ current_password, new_password }) => {
    try {
      await customerApi.post("/change-password", { current_password, new_password });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatStoreError(err) };
    }
  };

  const isAuthed = !!customer && customer !== false;

  return (
    <CustomerAuthContext.Provider
      value={{
        customer, loading, isAuthed,
        signIn, signUp, signOut,
        updateProfile, changePassword,
        requestPasswordReset, resetPassword,
        refresh: checkSession,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error("useCustomerAuth must be used within CustomerAuthProvider");
  return ctx;
}
